import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-layout-item>` says about itself.
 *
 * ── SIZES ARE NAMED BREAKPOINTS, NEVER PIXELS ────────────────────────────────
 * A size for a width is written against one of the platform's three breakpoints — `small`, `medium`,
 * `large` — and there is no input that takes a pixel width. That is the half of the pair the ticket
 * was built to protect: a raw width authored on a column is the defect the axis exists to remove.
 *
 * ── WHAT AN EXAMPLE CANNOT SHOW ──────────────────────────────────────────────
 * The breakpoint sizes answer to the width of the WINDOW, so the device-specific examples draw at the
 * width the reference page is read at. Their summaries say what changes at each breakpoint, because
 * narrowing the stage cannot show it.
 */
const docs: ComponentDocs = {
  aliases: ['column', 'grid column', 'layout column', 'cell', 'grid item', 'flex item'],

  answers: [
    'make a column half the width of the row',
    'make a column full width on a phone and half on a wide screen',
    'let one column take whatever space the others leave',
    'push a column to the far end of the row',
  ],

  placements: ['recordPage', 'homePage', 'appPage'],

  inputs: [
    {
      name: 'size',
      attribute: 'size',
      type: 'number',
      description:
        'How many twelfths of the row the item takes at every width, from 1 to 12. Absent, the item is as ' +
        'wide as what it holds.',
    },
    {
      name: 'smallSize',
      attribute: 'small-size',
      type: 'number',
      description: 'The twelfths the item takes from the small breakpoint (520px) up, until a larger size takes over.',
    },
    {
      name: 'mediumSize',
      attribute: 'medium-size',
      type: 'number',
      description: 'The twelfths the item takes from the medium breakpoint (720px) up, until a large size takes over.',
    },
    {
      name: 'largeSize',
      attribute: 'large-size',
      type: 'number',
      description: 'The twelfths the item takes from the large breakpoint (1200px) up.',
    },
    {
      name: 'flexibility',
      attribute: 'flexibility',
      type: 'text',
      options: ['auto', 'grow', 'shrink', 'no-grow', 'no-shrink', 'no-flex'],
      description:
        'How the item shares the row: auto grows and shrinks to fill it, grow and shrink do one of the two, ' +
        'no-grow and no-shrink refuse one, and no-flex refuses both. auto and grow let the item grow past ' +
        'its size.',
    },
    {
      name: 'padding',
      attribute: 'padding',
      type: 'text',
      options: ['horizontal-small', 'horizontal-medium', 'horizontal-large', 'around-small', 'around-medium', 'around-large'],
      description: 'Space between the item’s edge and what it holds, on its two sides or all the way round.',
    },
    {
      name: 'alignmentBump',
      attribute: 'alignment-bump',
      type: 'text',
      options: ['start', 'end', 'top', 'bottom'],
      description:
        'Pushes the item away from one edge: start sends it to the end of the row, end holds the items after ' +
        'it at the end, and top and bottom do the same across the row.',
    },
  ],

  slots: [
    {
      name: '',
      takes: 'Whatever the column holds — any placed piece, including another Layout to split the column again.',
    },
  ],

  parts: [{ part: 'item', is: 'The column’s box, inside the item’s padding.' }],

  tokens: [
    { token: '--caos-space-2', controls: 'The small padding.' },
    { token: '--caos-space-3', controls: 'The medium padding.' },
    { token: '--caos-space-4', controls: 'The large padding.' },
  ],

  placing:
    'Place it directly inside a Layout and put the column’s pieces inside it. Give it a size for every width, ' +
    'then a size for each breakpoint where it should change: size 12 with a medium size of 6 is a full-width ' +
    'column on a phone that becomes half the row from the medium breakpoint up. Sizes that add up past 12 on ' +
    'one line wrap the next item onto the line below. A size is a whole number from 1 to 12; anything else ' +
    'is ignored, and the item falls back to the size it has for a smaller width.',

  whenToUse: 'Every column of a Layout is one of these. It has no job outside a Layout.',

  insteadUse: [
    {
      name: 'Layout',
      when: 'You are deciding how the columns share the row — alignment along it or across it — rather than how wide one column is.',
    },
  ],

  accessibility:
    'An item adds no role: it is a column’s width, not a region, so a screen reader hears only what it holds. ' +
    'Sizes never reorder anything; items are read in the order they were placed at every width.',

  examples: [
    {
      key: 'content-width',
      label: 'As wide as what it holds',
      summary: 'Items with no size and no flexibility, each taking just the width of its own content.',
      placement: {
        component: 'caos-layout',
        children: [
          { component: 'caos-layout-item', children: [{ component: 'caos-tag', children: ['Short'] }] },
          { component: 'caos-layout-item', children: [{ component: 'caos-tag', children: ['A little longer'] }] },
        ],
      },
    },
    {
      key: 'auto',
      label: 'Filling the row',
      summary: 'Items set to auto, growing to share the whole row between them.',
      placement: {
        component: 'caos-layout',
        children: [
          { component: 'caos-layout-item', inputs: { flexibility: 'auto' }, children: [{ component: 'caos-tag', children: ['Grows'] }] },
          { component: 'caos-layout-item', inputs: { flexibility: 'auto' }, children: [{ component: 'caos-tag', children: ['Grows too'] }] },
        ],
      },
    },
    {
      key: 'no-grow',
      label: 'One column takes the rest',
      summary: 'A fixed column that refuses to grow beside an auto column that takes whatever remains.',
      placement: {
        component: 'caos-layout',
        children: [
          { component: 'caos-layout-item', inputs: { flexibility: 'no-grow' }, children: [{ component: 'caos-tag', children: ['Stays narrow'] }] },
          { component: 'caos-layout-item', inputs: { flexibility: 'auto' }, children: [{ component: 'caos-tag', children: ['Takes the rest'] }] },
        ],
      },
    },
    {
      key: 'sizes',
      label: 'Sized in twelfths',
      summary: 'Three items at a quarter, a half and a quarter of the row.',
      placement: {
        component: 'caos-layout',
        children: [
          { component: 'caos-layout-item', inputs: { size: 3 }, children: [{ component: 'caos-tag', children: ['3 of 12'] }] },
          { component: 'caos-layout-item', inputs: { size: 6 }, children: [{ component: 'caos-tag', children: ['6 of 12'] }] },
          { component: 'caos-layout-item', inputs: { size: 3 }, children: [{ component: 'caos-tag', children: ['3 of 12'] }] },
        ],
      },
    },
    {
      key: 'every-breakpoint',
      label: 'A size for each breakpoint',
      summary:
        'Full width on a phone, half from the small breakpoint, a third from medium and a quarter from large.',
      placement: {
        component: 'caos-layout',
        children: [1, 2, 3, 4].map((n) => ({
          component: 'caos-layout-item',
          inputs: { size: 12, smallSize: 6, mediumSize: 4, largeSize: 3 },
          children: [{ component: 'caos-tag', children: [`Item ${n}`] }],
        })),
      },
    },
    {
      key: 'medium-and-up',
      label: 'Side by side from medium up',
      summary: 'Two items that stack below the medium breakpoint and stand side by side at half width above it.',
      placement: {
        component: 'caos-layout',
        children: [
          { component: 'caos-layout-item', inputs: { size: 12, mediumSize: 6 }, children: [{ component: 'caos-tag', children: ['Left from medium up'] }] },
          { component: 'caos-layout-item', inputs: { size: 12, mediumSize: 6 }, children: [{ component: 'caos-tag', children: ['Right from medium up'] }] },
        ],
      },
    },
    {
      key: 'padding',
      label: 'Padded sides',
      summary: 'Items inset from their neighbours by padding on their two sides.',
      placement: {
        component: 'caos-layout',
        children: [
          { component: 'caos-layout-item', inputs: { size: 6, padding: 'horizontal-medium' }, children: [{ component: 'caos-tag', children: ['Padded'] }] },
          { component: 'caos-layout-item', inputs: { size: 6, padding: 'horizontal-medium' }, children: [{ component: 'caos-tag', children: ['Padded'] }] },
        ],
      },
    },
    {
      key: 'alignment-bump',
      label: 'Pushed to the end',
      summary: 'One item bumped from its start edge, so it sits at the far end while the others stay at the start.',
      placement: {
        component: 'caos-layout',
        children: [
          { component: 'caos-layout-item', children: [{ component: 'caos-tag', children: ['Start'] }] },
          { component: 'caos-layout-item', children: [{ component: 'caos-tag', children: ['Also start'] }] },
          { component: 'caos-layout-item', inputs: { alignmentBump: 'start' }, children: [{ component: 'caos-tag', children: ['Pushed to the end'] }] },
        ],
      },
    },
  ],
};

export default docs;
